import { Validators } from '@angular/forms';

export function getTransactionTypesForCurrency(
  transactionTypes: any[],
  currency: string,
): any[] {
  if (!currency) {
    return [];
  }

  return (transactionTypes ?? []).filter((type: any) =>
    transactionTypeMatchesCurrency(type, currency),
  );
}

export function getFallbackCashoutTransactionType(
  currency: string,
): string | null {
  const normalized = (currency ?? '').trim().toUpperCase();
  if (normalized === 'AED' || normalized === 'USD') {
    return 'Transferencia bancaria';
  }
  if (
    normalized === 'BRL' ||
    normalized === 'CLP' ||
    normalized === 'PEN' ||
    normalized === 'ARS'
  ) {
    return 'Transferencia bancaria';
  }
  if (normalized === 'MXN') {
    return 'SPEI Transfer';
  }
  if (normalized === 'BTC' || normalized === 'ETH' || normalized === 'USDT') {
    return 'Transferencia crypto';
  }
  return null;
}

export function transactionTypeMatchesCurrency(
  txType: any,
  currency: string,
): boolean {
  if (!txType) {
    return false;
  }

  const normalizedCurrency = (currency ?? '').trim().toUpperCase();
  if (!normalizedCurrency) {
    return false;
  }

  const currencies = normalizeCurrencyCodes(txType.currency);

  if (!currencies.length) {
    return true;
  }

  return currencies.some(
    (code) =>
      code === normalizedCurrency ||
      code === 'ALL' ||
      code === '*' ||
      code === 'ANY',
  );
}

export function normalizeCurrencyCodes(raw: any): string[] {
  const result = new Set<string>();
  const visited = new Set<any>();

  const visit = (value: any) => {
    if (value == null) {
      return;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return;
      }

      if (
        (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
        (trimmed.startsWith('{') && trimmed.endsWith('}'))
      ) {
        try {
          const parsed = JSON.parse(trimmed);
          visit(parsed);
          return;
        } catch {
          // Si falla el parse, seguimos usando el texto original.
        }
      }

      trimmed
        .split(/[,\|;]+/)
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((part) => result.add(part.toUpperCase()));

      return;
    }

    if (Array.isArray(value)) {
      if (visited.has(value)) {
        return;
      }
      visited.add(value);
      value.forEach(visit);
      return;
    }

    if (typeof value === 'object') {
      if (visited.has(value)) {
        return;
      }

      visited.add(value);

      if ('currency' in value) {
        visit((value as any).currency);
      }

      if ('currencies' in value) {
        visit((value as any).currencies);
      }

      if ('code' in value) {
        visit((value as any).code);
      }

      if ('codes' in value) {
        visit((value as any).codes);
      }

      return;
    }

    result.add(String(value).trim().toUpperCase());
  };

  visit(raw);

  return Array.from(result).filter(Boolean);
}

export function isBankTransfer(value: any): boolean {
  if (!value) {
    return false;
  }

  if (typeof value === 'object') {
    if ('id' in value) {
      value = value.id;
    } else if ('name' in value) {
      value = value.name;
    } else if ('value' in value) {
      value = value.value;
    } else {
      value = value.toString();
    }
  }

  return normalizeComparableText(value) === 'transferencia bancaria';
}

export function applyTransactionTypeValidators(
  control: any,
  transactionTypes: any[],
): void {
  if (!control) {
    return;
  }

  if (!transactionTypes.length) {
    control.clearValidators();
  } else {
    control.setValidators(Validators.required);
  }
  control.updateValueAndValidity({ emitEvent: false });
}

function normalizeComparableText(value: any): string {
  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}
